import type { SchematizedValues } from "components/Form/components/SchemaForm/SchemaForm.types";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import {
    DeleteListAction,
    deleteActionSignature,
    deletePermanentlyActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
    valueAndSchema,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { isEqual } from "lodash";
import { egressPolicyRulesSchema } from "./egressPolicyRulesSchema";
import resource from "./resource";

const resourceName = "egress_policy";

// The outbound gate is enforced in the connector execution path (not the prompt),
// so a policy here controls which external destinations a connection may reach.
// `rules` is a list (order does not matter: deny > allow > policy default) of
// { connector_type, match: { field: glob, ... }, effect: allow | deny }. It is
// rendered as a structured SchemaForm (egressPolicyRulesSchema) with the
// SchemaForm component's built-in Code-view toggle as the raw-YAML escape hatch.

const config: ResourceConfig = {
    label: "Egress policy",
    icon: IconCollection.security,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "egressPolicyUserImpact" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    actions: {
        shown: [],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: () => [
        {
            label: "Enabled",
            key: "enabled",
            shown: true,
            sortable: true,
            dto: (value: { enabled?: boolean }) => (value?.enabled ? "Yes" : "No"),
        },
        {
            label: "Scope",
            key: "scope",
            shown: true,
            dto: (value: { scope_id_resource?: { name?: string } | null }) =>
                value?.scope_id_resource?.name ?? "Workspace (global)",
        },
        {
            label: "Rules",
            key: "rule_count",
            shown: true,
            dto: (value: { rules?: unknown[] | null }) =>
                Array.isArray(value?.rules) ? value.rules.length : 0,
        },
        {
            label: "Audit",
            key: "audit",
            shown: false,
            sortable: true,
            dto: (value: { audit?: boolean }) => (value?.audit ? "Yes" : "No"),
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            enabled
            audit
            scope_id
            scope_id_resource {
                id
                name
                record_type
            }
            rules
            on_allow_flow_id
            on_allow_flow_id_flow {
                id
                name
            }
            on_deny_flow_id
            on_deny_flow_id_flow {
                id
                name
            }
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            enabled
            audit
            scope_id
            scope_id_resource {
                id
                name
                record_type
            }
            rules
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $enabled: Boolean,
            $audit: Boolean,
            $scope_id: String,
            $rules: SchemalessData,
            $on_allow_flow_id: String,
            $on_deny_flow_id: String,
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            enabled: $enabled,
            audit: $audit,
            scope_id: $scope_id,
            rules: $rules,
            on_allow_flow_id: $on_allow_flow_id,
            on_deny_flow_id: $on_deny_flow_id,
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $enabled: Boolean,
            $audit: Boolean,
            $scope_id: String,
            $rules: SchemalessData,
            $on_allow_flow_id: String,
            $on_deny_flow_id: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            enabled: $enabled,
            audit: $audit,
            scope_id: $scope_id,
            rules: $rules,
            on_allow_flow_id: $on_allow_flow_id,
            on_deny_flow_id: $on_deny_flow_id,
        `,
    },
    form: [
        {
            name: "enabled",
            component: "switch",
            fieldProps: {
                label: "Enabled",
                defaultValue: false,
                docs: {
                    text: "When off, this policy is ignored. A new policy starts disabled so you can review its rules before it governs connections.",
                },
            },
        },
        {
            // Render `rules` as a structured SchemaForm (discriminated union
            // over the channel = connector_type) driven by the static
            // egressPolicyRulesSchema. The SchemaForm component's built-in
            // "Code view" toggle preserves a raw-YAML escape hatch for any rule
            // shape the form cannot represent. The schema is a static module
            // constant, not a persisted field — only the edited rules map back
            // to `rules` (remappingSubmitDto), so the field is named
            // valueAndSchema (mirrors dashboard.tsx / flow.tsx).
            name: valueAndSchema,
            component: "schemaForm",
            fieldProps: {
                lang: "yaml",
                isReadOnly: false,
                canEnableSaveManually: true,
                docs: {
                    text:
                        "The rules of this policy. Each rule names a channel " +
                        "(SMTP, HTTP/REST, SSH, SCP, Git, or any) and an allow/deny effect; its " +
                        "Match fields constrain the destination (glob patterns). A matching Deny " +
                        "rule always wins over a matching Allow rule, so the order of the rules " +
                        "does not matter. If no rule matches, the policy's default applies.",
                },
            },
            remappingSubmitDto: (value: SchematizedValues) => [
                {
                    key: "rules",
                    value: Array.isArray(value?.values?.rules) ? value.values.rules : [],
                },
            ],
            isEqual: (valueA: SchematizedValues, valueB: SchematizedValues) =>
                isEqual(valueA?.values?.rules, valueB?.values?.rules),
        },
        {
            name: "scope_id",
            component: "autocomplete",
            fieldProps: {
                label: "Scope",
                // The scope chain the gate ranks is connector (most specific) ->
                // its bundle -> its project -> the workspace-wide default (empty).
                // A single record picker spans all three eligible record types by
                // querying the generic `record` resource filtered to them, storing
                // the chosen record id. Leaving it empty targets the global policy.
                options: {
                    resource: "record",
                    resourceBody: "record_type",
                    queryField: {
                        field: "record_type",
                        op: "in",
                        value: ["CONNECTOR", "BUNDLE", "PROJECT"],
                    },
                },
                docs: {
                    text: "The connector, bundle or project this policy applies to. A connector-scoped policy wins over one scoped to its bundle, which wins over one scoped to its project. Leave empty for the workspace-wide default policy.",
                },
            },
        },
        {
            name: "on_deny_flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "On-deny hook flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Optional flow run (fire-and-forget) when this policy denies a connection — for example to raise an alert or request approval. It reacts only; it does not change the verdict.",
                },
            },
        },
        {
            name: "on_allow_flow_id",
            component: "autocomplete",
            fieldProps: {
                label: "On-allow hook flow",
                options: {
                    resource: "flow",
                },
                docs: {
                    text: "Optional flow run (fire-and-forget) when this policy allows a connection. It reacts only; it does not change the verdict.",
                },
            },
        },
        {
            name: "audit",
            component: "switch",
            fieldProps: {
                label: "Audit",
                defaultValue: false,
                docs: {
                    text: "When on, each verdict writes a log entry on this policy (shown in the Logs tab) recording the connection channel and destination.",
                },
            },
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const valueAndSchemaFieldValues: SchematizedValues = {
                values: { rules: Array.isArray(data?.rules) ? data.rules : [] },
                schema: egressPolicyRulesSchema,
            };
            return { ...data, [valueAndSchema]: valueAndSchemaFieldValues };
        },
    },
};
export default config;
